<script lang="ts" setup>

import { toTypedSchema } from '@vee-validate/zod';
import dayjs from 'dayjs';
import { useField, useForm } from 'vee-validate';
import type { Address } from 'viem';
import * as zod from 'zod';

import { formatNumber } from '@/utils/helpers';
import {
    computed,
    isValidNumber,
    ref,
    useAlertsStore,
    useEventModalsStore,
    useListing,
    useProviderConfig,
    watch
} from '#imports';
import type { CollectionApprovedTokens } from '~/service/api/types';
import type { EvmItemType } from '~/service/EVM/types';

const props = defineProps<{
    data: Pick<AppWTFNft,
    'uuid' |
    'address' |
    'name' |
    'price' |
    'network' |
    'identifier' |
    'displayImageURL' |
    'itemType' |
    'ownedCount'
    > & { collectionName?: string; slug: string };
    modelValue: boolean;
    refreshNftData?: () => void;
}>();

const calendar = ref<HTMLElement | null>(null);
const dateInput = ref<HTMLElement | null>(null);
const overlay = ref<HTMLElement | null>(null);
const isDatePickerVisible = ref(false);

const initiatorKey = `ListingOrder-${props.data.uuid}`;

const isLoading = useState(initiatorKey, () => false);

const { createListingOrder } = useListing(initiatorKey);

const alertStore = useAlertsStore();
const modalEventsStore = useEventModalsStore();
const { eventEmitter } = useEVMCore();
const { collection, QueryEvmKey } = useEVMApi();

const {
    data: commissions,
    status: statusCommissions
} = useAsyncData(
    `${props.data.slug}-${QueryEvmKey.COMMISSION_BY_SLUG}`,
    async () => {
        const { data } = await collection.commissionById(props.data.slug);
        return data;
    },
    {
        default: () => []
    }
);

const {
    data: marketContract,
    status: marketContractsStatus
} = useAsyncData(
    QueryEvmKey.MARKETPLACE_CONTRACTS,
    async () => {
        const { data } = await collection.contractSettingsList();
        return data;
    },
    {
        transform: data => data.find(item => props.data.network === item.network),
        default: () => null
    }
);

const requiredCommissionPercent = computed(() => commissions.value
    ? commissions.value
        .filter(item => item.required)
        .reduce((sum, item) => sum + item.fee, 0) * 100
    : null);

const { data: approvedErc20Tokens, status: statusApprovedErc20Tokens } = useAsyncData(
    `${props.data.slug}-${QueryEvmKey.COLLECTION_APPROVED_TOKENS}`,
    async () => {
        if (props.data.network) {
            const { data } = await collection.approvedErc20Tokens(props.data.network);
            return data;
        }
        return [];
    },
    {
        default: () => []
    }
);

const formattedSelectedDate = computed(() => {
    if (isDatePickerVisible.value) {
        return dayjs(durationValue.value).format('DD.MM.YYYY ');
    }
    else {
        return dayjs(durationValue.value).format('MMM DD ');
    }
});

const formattedSelectedTime = computed(() => {
    if (isDatePickerVisible.value) {
        return dayjs(durationValue.value).format('HH:mm');
    }
    else {
        return dayjs(durationValue.value).format('hh:mm A');
    }
});

const toggleDatePicker = () => {
    isDatePickerVisible.value = !isDatePickerVisible.value;
};

const durations = [
    {
        name: '1 Day',
        value: dayjs().add(1, 'day').format()
    },
    {
        name: '1 Week',
        value: dayjs().add(1, 'week').format()
    },
    {
        name: '1 Month',
        value: dayjs().add(1, 'month').format()
    }
];

enum SaleVariantEnum {
    FIXED_PRICE,
    SELL_TO_HIGHEST
}

const tabs721 = [
    {
        label: 'Fixed price',
        value: SaleVariantEnum.FIXED_PRICE,
        description: 'The item is listed at the price you set'
    },
    {
        label: 'Sell to highest bidder',
        value: SaleVariantEnum.SELL_TO_HIGHEST,
        description: 'The item is listed for auction.'
    }
];

const emit = defineEmits<{
    (e: 'update:modelValue', payload: boolean): void;
}>();

const {
    constants: { NETWORK_NAME, MARKETPLACE_NAME },
    utils: { isEvmErc1155, isEvmErc721 },
    map: { chainMap }
} = useProviderConfig()(['constants', 'utils', 'map']);

const minValue = computed(() => ({
    [NETWORK_NAME.MAINNET]: { precision: 4 },
    [NETWORK_NAME.POLYGON]: { precision: 6 },
    [NETWORK_NAME.XSOLLA_ZK]: { precision: 4 }
}));

const selectedTab721 = ref(SaleVariantEnum.FIXED_PRICE);

// const defaultEarningPercent = 0;

const validationSchema
    = zod.object({
        price: zod
            .preprocess(
                val => (typeof val === 'number' ? val.toString() : val),
                zod
                    .string()
                    .refine(val => val !== null && val !== undefined && isValidNumber(val) && Number(val) !== 0, { message: 'Invalid number format' })
                    .transform(val => Number(val))
            ),
        duration: zod.string().nullable(),
        currency: zod.object({
            address: zod.string(),
            decimals: zod.number(),
            itemType: zod.number(),
            symbol: zod.string(),
            minPrice: zod.number()
        }).nullable(),
        amount: zod
            .preprocess(
                val => (typeof val === 'number' ? val.toString() : val),
                zod
                    .string()
                    .optional()
                    .refine(val => val !== null && val !== undefined && isValidNumber(val) && Number(val) !== 0, { message: 'Invalid number format' })
                    .transform(val => Number(val))
                    .refine(val => val <= (props.data.ownedCount ?? 0), {
                        message: `You can make listing for up to ${props.data.ownedCount || 0} tokens`
                    })
            )
        // creatorEarning: zod.object({
        //     label: zod.string(),
        //     value: zod.number().refine(val => val <= defaultEarningPercent, { message: `The value cannot be greater than ${defaultEarningPercent}%` })
        // })
    }).superRefine((data, ctx) => {
        if (data.currency && data.price < data.currency.minPrice) {
            ctx.addIssue({
                code: zod.ZodIssueCode.custom,
                path: ['price'],
                message: `Offer must be at least the minimum price of ${data.currency.minPrice} ${data.currency.symbol}`
            });
        }
    });

type FormValuesType = zod.infer<typeof validationSchema>;

// const creatorEarningInitial = [
//   {
//     label: 'Default',
//     value: defaultEarningPercent
//   },
//   {
//     label: 'Custom',
//     value: defaultEarningPercent,
//     isEditable: true
//   }
// ];

const {
    handleSubmit,
    values,
    meta,
    errors
} = useForm<FormValuesType>({
    validationSchema: toTypedSchema(validationSchema),
    initialValues: {
    // creatorEarning: {
    //     value: defaultEarningPercent,
    //     label: 'Default'
    // },
        currency: null,
        amount: 1
    }
});

const { value: priceValue, errorMessage: priceValueError } = useField<string>('price');

const { value: amountValue, errorMessage: amountValueError } = useField<string>('amount');

const {
    value: currencyValue,
    setValue: setCurrencyValue
} = useField<CollectionApprovedTokens>('currency');

// const {
//     value: creatorEarningValue,
//     errorMessage: creatorEarningError
// } = useField<OptionValue<number>>('creatorEarning');

const {
    value: durationValue,
    setValue,
    errorMessage: durationValueError
} = useField<string>('duration');

watch([approvedErc20Tokens], ([newApprovedErc20Tokens]) => {
    if (newApprovedErc20Tokens.length && newApprovedErc20Tokens[0]) {
        setCurrencyValue(newApprovedErc20Tokens[0]);
    }
});

const handleSelectedDateTimeUpdate = (dateTime: string) => {
    setValue(dateTime);
};

const buttonLoading = computed(() =>
    isLoading.value
    || statusCommissions.value === 'pending'
    || statusApprovedErc20Tokens.value === 'pending'
    || marketContractsStatus.value === 'pending'
);
// const fees = computed(() => {
//     const price = Number(values.price || 0);
//     const amount = Number(values.amount || 1);
//     // const creatorEarning = Number(values.creatorEarning?.value || 0);
//
//     if (!price) {
//         return null;
//     }
//
//     const percentValue = (price * amount) / 100;
//     const marketplace = percentValue * FEES.PERCENT_VALUE;
//     // const creator = creatorEarning ? percentValue * creatorEarning : 0;
//     const total = marketplace;
//     // + creator;
//
//     return {
//         marketplace,
//         // creator,
//         total
//     };
// });

const listingPrice = computed(() => values.amount ? values.price * values.amount : values.price);

const totalPrice = computed(() => {
    const price = Number(values.price || 0);
    const amount = Number(values.amount || 1);

    if (!requiredCommissionPercent.value) {
        return null;
    }

    const allSum = price * amount;

    const totalFees = (allSum * requiredCommissionPercent.value) / 100;

    const total = allSum - totalFees;

    if (total < 0) {
        return null;
    }

    const precision = minValue.value[props.data.network]?.precision || 4;

    return {
        formatted: formatNumber(total, {
            precision: precision
        }),
        total
    };
});

const onSubmit = handleSubmit(async ({
    price, duration, amount, currency
    // creatorEarning
}) => {
    if (!requiredCommissionPercent.value || !marketContract.value) {
        return alertStore.add({
            critical: true,
            description: `It is not possible to create an order now, try again later`,
            status: 'type.error',
            title: 'Something went wrong'
        });
    }

    const total = price * amount;

    if (duration && currency) {
        const otherRecipients = commissions.value.map(item => ({
            value: item.fee * 100,
            address: item.address,
            token: currency.address as Address,
            itemType: currency.itemType as EvmItemType
        }));

        // TODO убрали из логики и из UI
        // ...(fees.value?.creator && props.content.collectionOwner
        //     ? [
        //             {
        //                 value: fees.value.creator,
        //                 address: props.content.collectionOwner
        //             } as EvmOtherRecipientType]
        //     : []
        // )

        await createListingOrder({
            value: total,
            contract: props.data.address,
            paramNetwork: props.data.network,
            endTime: duration,
            identifier: props.data.identifier,
            // add dynamic markeplace after add owners
            marketContract: marketContract.value,
            marketplace: MARKETPLACE_NAME,
            uuid: props.data.uuid,
            itemType: props.data.itemType,
            currencyTokenAddress: currency.address as Address,
            currencyItemType: currency.itemType as EvmItemType,
            otherRecipients,
            ...(amount
                ? { amount: Number(amount) }
                : {})
        });
    }
});

const calendarStyle = computed(() => {
    if (dateInput.value && overlay.value && calendar.value) {
        const dateInputRect = dateInput.value.getBoundingClientRect();
        const overlayRect = overlay.value.getBoundingClientRect();

        // Смещение dateInput относительно overlay
        const offsetX = overlayRect.right - dateInputRect.right;
        const offsetY = dateInputRect.top - overlayRect.top;

        const calendarElHeight = 453;

        return {
            top: `${offsetY - calendarElHeight - 4}px`,
            right: `${offsetX}px`
        };
    }
    return null;
});

const getDescriptionForModal = (itemType: EvmItemType) => {
    let result: string[];
    result = [];
    if (isEvmErc721(itemType)) {
        result = [`${totalPrice.value?.formatted} ${currencyValue.value?.symbol} Chain: ${props.data.network}`];
    }
    else if (isEvmErc1155(itemType)) {
        result = [`${amountValue.value} item ${totalPrice.value?.formatted} ${currencyValue.value?.symbol}`, `Chain: ${props.data.network}`];
    }

    return result;
};

onMounted(() => {
    eventEmitter.on('fullListingProcess', {
        key: initiatorKey,
        onSuccess: () => {
            if (props.modelValue) {
                emit('update:modelValue', false);
                if (props?.refreshNftData) props.refreshNftData();
            }
            isLoading.value = false;
            modalEventsStore.add(
                {
                    status: '',
                    type: 'listed',
                    images: [props.data.displayImageURL ? props.data.displayImageURL : ''],
                    imageState: 'default',
                    title: `${props.data?.name}`,
                    description: getDescriptionForModal(props.data.itemType),
                    heading: 'Your items are listed!',
                    itemType: props.data.itemType,
                    content: `${amountValue.value} ${props.data?.name} items from the ${props.data?.collectionName} collection are for sale.`,
                    button: {
                        callback: () => modalEventsStore.removeByType('listed'),
                        title: 'View listed'
                    }
                });
        },
        onError: () => {
            isLoading.value = false;
            modalEventsStore.add(
                {
                    status: '',
                    type: 'error',
                    images: [props.data.displayImageURL ? props.data.displayImageURL : ''],
                    imageState: 'error',
                    title: `${props.data?.name}`,
                    description: getDescriptionForModal(props.data.itemType),
                    heading: 'Error',
                    itemType: props.data.itemType,
                    content: 'Something went wrong, Try again later.'
                });
        },
        onLoad: (loading) => {
            isLoading.value = loading;
            if (loading) {
                modalEventsStore.add(
                    {
                        status: '',
                        type: 'approve',
                        images: [props.data.displayImageURL ? props.data.displayImageURL : ''],
                        imageState: 'loading',
                        title: `${props.data?.name}`,
                        description: getDescriptionForModal(props.data.itemType),
                        heading: 'Approve listing',
                        itemType: props.data.itemType,
                        content: 'You\'ll be asked to review and confirm this listing from your wallet.'
                    });
            }
        }
    });
});

onUnmounted(() => {
    eventEmitter.off('fullListingProcess', 'ModalListingComponent');
});

</script>

<template>
  <form class="display-flex flex-column align-items-start width-full modal"
        @submit.prevent="onSubmit"
  >
    <h2 class="display-800-accent">
      List for sale
    </h2>

    <div class="display-flex gap-16 align-items-center width-full modal-info">
      <ImgMain :alt="data.price?.token"
               :src="data.displayImageURL"
               class="modal-info__image"
      />
      <span>
        <span class="ml-4">{{ data?.name }}</span>
      </span>
    </div>

    <div class="modal-content">
      <div class="display-flex flex-column width-full gap-24 modal-block modal--vertical">
        <ToggleIconGroup v-if="isEvmErc721(data.itemType)"
                         v-model="selectedTab721"
                         :items="tabs721"
                         class="width-full"
                         is-disabled
        >
          <template #custom="{ item }">
            <div class="modal-tab-price">
              <h5 class="compact-500-accent">{{ item.label }}</h5>
              <p class="compact-400-default">{{ item.description }}</p>
            </div>
          </template>
        </ToggleIconGroup>

        <div v-if="isEvmErc1155(data.itemType)"
             class="display-flex flex-column width-full gap-8 position-relative"
        >
          <div class="display-flex flex-row justify-between">
            <p class="compact-500-accent mb-4">
              Quantity of items
            </p>
            <span class="compact-500-accent text-color-secondary">{{ data.ownedCount }} available</span>
          </div>

          <input v-model="amountValue"
                 aria-label="Quantity of items"
                 class="base-input compact-400-default"
                 name="price"
                 placeholder="Enter a quantity"
                 type="number"
          >
          <Transition mode="out-in"
                      name="fade"
          >
            <span v-if="amountValueError"
                  class="position-absolute compact-300-accent price-error"
            >{{ amountValueError }}</span>
          </Transition>
        </div>

        <div class="position-relative">
          <div class="display-flex align-items-center width-full gap-4">
            <input v-model="priceValue"
                   aria-label="price"
                   class="base-input compact-400-default"
                   inputmode="decimal"
                   name="price"
                   placeholder="Price"
            >
            <LoaderDots v-if="statusApprovedErc20Tokens === 'pending'" />
            <FormSelect v-else-if="approvedErc20Tokens"
                        :initial-value="currencyValue"
                        :is-disabled="approvedErc20Tokens.length <= 1"
                        :options="approvedErc20Tokens"
                        container-class="modal-price__select"
                        label="symbol"
                        placeholder="Select"
                        track-by="symbol"
                        @on-change="(payload) => setCurrencyValue(payload)"
            />
          </div>
          <Transition mode="out-in"
                      name="fade"
          >
            <span v-if="priceValueError"
                  class="position-absolute compact-300-accent modal-price__error"
            >{{ priceValueError }}</span>
          </Transition>
        </div>

        <div class="display-flex flex-column width-full gap-16">
          <p class="compact-500-accent">
            Duration
          </p>

          <div class="display-flex align-items-center justify-between">
            <FormSelect :error="durationValueError"
                        :options="durations"
                        container-class="modal-duration__select"
                        label="name"
                        track-by="name"
                        @on-change="(payload) => setValue(payload.value)"
            />

            <div v-if="durationValue"
                 ref="dateInput"
                 :class="['base-input cursor-pointer', { 'border-none': !isDatePickerVisible }]"
                 @click="toggleDatePicker">
              <div class="display-flex align-items-center gap-16">
                <p class="compact-400-accent color-text-grey">{{ formattedSelectedDate }}</p>
                <p class="compact-400-numeric color-text-grey">{{ formattedSelectedTime }}</p>
              </div>
            </div>

            <div ref="overlay"
                 :class="['position-relative', isDatePickerVisible ? 'user-select pointer-events': 'user-select-none pointer-events-none']"
                 class="overlay"
                 @click="toggleDatePicker">
              <div id="calendar-teleport-target"
                   ref="calendar"
                   :class="['user-select', 'pointer-events', 'position-absolute', isDatePickerVisible ? '' : 'none']"
                   :style="calendarStyle"
                   @click.stop>
                <LazyDatePicker v-if="isDatePickerVisible"
                                :disable-past-days="true"
                                :init-date="durationValue"
                                @close=toggleDatePicker
                                @update:selectedDateTime="handleSelectedDateTimeUpdate"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!--      <div class="modal-earnings width-full modal&#45;&#45;vertical">-->
      <!--        <p class="compact-500-accent mb-8">Creator earnings</p>-->
      <!--        <RadioSwitchGroup v-model="creatorEarningValue"-->
      <!--                         :item-label-error="!!creatorEarningError ? { value: creatorEarningError, label: creatorEarningValue.label } : undefined"-->
      <!--                         :items="creatorEarningInitial"-->
      <!--        />-->
      <!--      </div>-->

      <div class="display-flex flex-column align-self-stretch gap-16 modal-total modal--vertical">
        <div class="display-flex align-items-center justify-between gap-16 width-full">
          <span class="compact-400-numeric text-color-secondary">Listing price</span>
          <span v-if="listingPrice"
                class="compact-400-numeric text-color-secondary"
          >{{ listingPrice }} {{ currencyValue?.symbol }}</span>
          <span v-else
                class="compact-400-numeric text-color-secondary"
          >-</span>
        </div>

        <div class="display-flex align-items-center justify-between gap-16 width-full">
          <span class="compact-400-numeric text-color-secondary">Fee</span>
          <span class="compact-400-numeric text-color-secondary"
          >{{ requiredCommissionPercent || "- " }} %</span>
        </div>

        <div class="display-flex align-items-center justify-between gap-16 mb-8 width-full"
        >
          <span class="compact-500-accent">Total potential earnings</span>
          <p class="compact-500-accent">
            <span v-if="totalPrice"
            >{{ totalPrice.formatted }}</span>
            <span v-else>--</span>
            {{ currencyValue?.symbol }}
          </p>
        </div>
      </div>
    </div>

    <Button :is-disabled="!meta.valid"
            :is-loading="buttonLoading"
            class="width-full"
            color="bg.mojito"
            size="size.medium"
    >
      <button class="display-flex justify-center gap-8 align-items-center"
              type="submit"
      >
        <span class="compact-500-accent">Complete listing</span>
        <IconMain :size="24"
                  icon="arrow-r-l"
        />
      </button>
    </Button>
  </form>
</template>

<style lang="scss">

.offer-token {
  width: 120px;
  height: 120px;
}

.wallet-offer {
  width: 52px;
  height: 52px;
  background: var(--bg-tertary);
}

.drop-input-offer {
  max-width: 324px !important;
  padding: 0 8px;
}

.btn-pad {
  padding-top: 30px;
}

</style>

<style lang="scss" scoped>

:deep(.modal-info__image) {
  width: 120px;
  height: 120px;
}

.modal {
  max-height: 100%;

  &-content {
    width: calc(100% + #{$spacing-400});
    margin-right: -$spacing-400;
    padding-right: $spacing-400;
    overflow: auto;
  }

  &--vertical {
    padding-right: 2px;
    padding-left: 2px;
  }

  &-total {
    margin-bottom: $spacing-400;
  }

  &-info {
    padding: 16px 0;
  }

  &-earnings {
    margin-bottom: 16px;
  }

  &-block {
    margin-bottom: 16px;
    padding-top: 16px;
    padding-bottom: 16px;
    border-top: 1px solid var(--border-primary);
    border-bottom: 1px solid var(--border-primary);
  }

  &-duration {
    &__select {
      width: 100%;
      max-width: 324px;
    }
  }

  &-price {
    &__select {
      width: 100%;
      max-width: 95px;
    }

    &__error {
      bottom: -18px;
      color: var(--content-negative);
    }
  }

  &-tab {
    &-price {
      padding: $spacing-300
    }
  }
}

.overlay {
  position: absolute;
  inset: 0;
}

#calendar-teleport-target {
  z-index: 2;
  width: 332px;
  height: 453px;
}

.border-none {
  border: none;
}

.user-select-none {
  user-select: none;
}

.pointer-events-none {
  pointer-events: none;
}

.user-select {
  user-select: initial;
}

.pointer-events {
  pointer-events: initial;
}

.none {
  display: none;
  visibility: hidden;
  position: absolute;
  z-index: -9999;
  width: 0;
  height: 0;
  margin: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  user-select: none;
}

.fade-enter-active {
  transition: all 0.3s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
